/**
 * Soft radial glow sprite for the Law-6 v2 green halo (#267).
 *
 * ONE cached data-URL canvas — every halo billboard passes the same image
 * string, so Cesium's billboard atlas stores a single texture no matter how
 * many markers glow (336 at country zoom costs one 64px texture + one batched
 * billboard collection). The sprite is WHITE with a radial falloff and is
 * tinted per-marker via the billboard's `color`, so it stays colour-agnostic
 * (the green family today; reusable if another family is ever ruled a glow).
 *
 * Returns null when no canvas is available (jsdom/SSR) — callers skip the
 * halo, the same guard idiom as renderLucideToDataUrl's glyphs.
 */
export const GLOW_TEXTURE_SIZE = 64;

let cached: string | null | undefined;

export function glowTextureDataUrl(): string | null {
  if (cached !== undefined) return cached;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = GLOW_TEXTURE_SIZE;
    canvas.height = GLOW_TEXTURE_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      cached = null;
      return cached;
    }
    const c = GLOW_TEXTURE_SIZE / 2;
    const g = ctx.createRadialGradient(c, c, 0, c, c, c);
    // Bright core → long soft tail. The tail is what reads as "glow" rather
    // than a hard translucent disc (the pre-#267 static halo).
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.7, 'rgba(255,255,255,0.18)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, GLOW_TEXTURE_SIZE, GLOW_TEXTURE_SIZE);
    cached = canvas.toDataURL('image/png');
  } catch {
    cached = null;
  }
  return cached;
}

/** Test seam: drop the cached sprite (jsdom canvas availability varies per test). */
export function __resetGlowTextureCache(): void {
  cached = undefined;
}
